import React, { useEffect, useState, useRef } from "react";
import { db } from "../firebase";
import emailjs from "emailjs-com";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingAnimation from "./LoadingAnimation";


import {
  doc,
  getDoc,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import {
  RESERVATION_TTL_MS,
  getOrCreateUserId,
  getOrCreateTabId,
  createSlotReservation,
  releaseReservation,
  confirmReservation,
  restoreReservation,
  getMinSelectableDate,
  formatDateToISO,
  formatToAppDateString,
  validateInterviewDate,
} from "../utils/slotReservation";

// ─── HTML ENTITY DECODER ────────────────────────────────────────────────────
function decodeHtml(str) {
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
}

// ─── ADMIN EMAIL BUILDER ────────────────────────────────────────────────────
function buildAdminEmail(data, jobTitle, resumeURL, emailLogo) {
  const year = new Date().getFullYear();
  const logoUrl = emailLogo || "https://i.postimg.cc/023xvzzm/vbt-logo-2.png";

  const rows = [
    ["&#128100;", "Full Name", data.name],
    ["&#128231;", "Email", data.email],
    ["&#128196;", "CNIC", data.cnic],
    ["&#128222;", "Phone", data.phone],
    ["&#128205;", "Location", data.location || data.Location], // safe fallback
    ["&#128197;", "Interview Date", data.interviewDate],
    ["&#128336;", "Interview Slot", data.interviewSlot],
    ["&#128188;", "Job Title", jobTitle],
  ];

  const tableRows = rows
    .map(function (row) {
      return (
        "<tr>" +
        "<td style='padding:12px 18px;width:40%;border-bottom:1px solid #f5f5f5;'>" +
        "<span style='font-size:16px;margin-right:8px;'>" + row[0] + "</span>" +
        "<span style='font-weight:600;color:#444;font-size:13px;text-transform:uppercase;letter-spacing:0.5px;'>" + row[1] + "</span>" +
        "</td>" +
        "<td style='padding:12px 18px;color:#111;font-size:14px;font-weight:500;border-bottom:1px solid #f5f5f5;'>" +
        row[2] +
        "</td>" +
        "</tr>"
      );
    })
    .join("");

  return (
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#f0f4f8;padding:36px 0;font-family:Segoe UI,Arial,sans-serif;'>" +
    "<tr><td align='center'>" +
    "<table width='600' cellpadding='0' cellspacing='0' style='background:#ffffff;border-radius:16px;overflow:hidden;'>" +

    // HEADER
    "<tr>" +
    "<td style='background:#ff8000;text-align:center;padding:36px 24px 28px;'>" +
    "<img src='" + logoUrl + "' width='160px' alt='VBT' style='display:block;margin:0 auto 18px;' />" +
    "<h1 style='margin:0;color:#ffffff;font-size:22px;font-weight:700;letter-spacing:0.3px;'>New Job Application Received</h1>" +
    "<p style='margin:8px 0 0;color:rgba(255,255,255,0.82);font-size:13px;'>Submitted via VisionBird Careers Portal</p>" +
    "</td>" +
    "</tr>" +

    // ALERT BANNER
    "<tr>" +
    "<td style='padding:24px 28px 0;'>" +
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#fff8f0;border-radius:10px;border:1px solid #ffd199;'>" +
    "<tr>" +
    "<td style='padding:4px 0 4px 14px;width:4px;background:#ff8000;border-radius:10px 0 0 10px;'></td>" +
    "<td style='padding:14px 16px;'>" +
    "<p style='margin:0;color:#b35900;font-size:13px;font-weight:600;'>&#128276; Action Required</p>" +
    "<p style='margin:4px 0 0;color:#804000;font-size:13px;'>Review this application and follow up if the candidate matches your requirements.</p>" +
    "</td>" +
    "</tr>" +
    "</table>" +
    "</td>" +
    "</tr>" +

    // APPLICANT DETAILS
    "<tr>" +
    "<td style='padding:24px 28px 8px;'>" +
    "<p style='margin:0 0 12px;font-size:11px;font-weight:700;color:#999;text-transform:uppercase;letter-spacing:1.2px;'>Applicant Details</p>" +
    "<table width='100%' cellpadding='0' cellspacing='0' style='border:1px solid #eeeeee;border-radius:10px;overflow:hidden;'>" +
    tableRows +
    "</table>" +
    "</td>" +
    "</tr>" +

    // RESUME BUTTON
    "<tr>" +
    "<td style='padding:20px 28px 32px;text-align:center;'>" +
    "<a href='" + resumeURL + "' target='_blank' rel='noopener noreferrer' style='display:inline-block;background:#ff8000;color:#ffffff;text-decoration:none;padding:14px 40px;border-radius:50px;font-weight:700;font-size:14px;letter-spacing:0.3px;'>View Resume</a>" +
    "</td>" +
    "</tr>" +

    // FOOTER
    "<tr>" +
    "<td style='background:#111111;text-align:center;padding:22px;'>" +
    "<p style='margin:0 0 4px;color:#ff8000;font-size:13px;font-weight:700;letter-spacing:0.5px;'>VISIONBIRD TECHNOLOGIES</p>" +
    "<p style='margin:0;color:#666;font-size:12px;'>&#169; " + year + " &nbsp;&#183;&nbsp; Automated Notification &nbsp;&#183;&nbsp; Do not reply</p>" +
    "</td>" +
    "</tr>" +

    "</table>" +
    "</td></tr>" +
    "</table>"
  );
}

/// ─── CANDIDATE ACKNOWLEDGMENT EMAIL (sent immediately on form submit) ────────
// Lightweight confirmation — does NOT include interview details or checklist.
// Interview details are only communicated when the admin sends a reply.
function buildApplicationAckEmail(data, jobTitle, emailLogo) {
  const year = new Date().getFullYear();
  const logoUrl = emailLogo || "https://i.po  stimg.cc/023xvzzm/vbt-logo-2.png";

  const progressSteps = [
    { icon: "&#10003;", label: "Application<br>Received", active: true },
    { icon: "&#128269;", label: "Profile<br>Under Review", active: false },
    { icon: "&#127897;", label: "Interview<br>Invitation", active: false },
  ];

  const progressCells = progressSteps
    .map(function (step, i) {
      const circle =
        "<div style='width:48px;height:48px;border-radius:50%;margin:0 auto 10px;" +
        "background:" + (step.active ? "#ff8000" : "#ebebeb") + ";" +
        "line-height:48px;font-size:20px;text-align:center;color:" + (step.active ? "#fff" : "#aaa") + ";'>" +
        step.icon +
        "</div>" +
        "<p style='margin:0;font-size:11px;font-weight:700;color:" + (step.active ? "#ff6600" : "#aaa") + ";text-align:center;line-height:1.5;'>" +
        step.label +
        "</p>";

      const cell = "<td style='text-align:center;padding:0 4px;'>" + circle + "</td>";

      if (i < progressSteps.length - 1) {
        const connector = "<td style='padding-bottom:32px;'>" +
          "<div style='height:2px;background:" + (step.active ? "#ffc680" : "#e8e8e8") + ";margin-top:24px;'></div>" +
          "</td>";
        return cell + connector;
      }
      return cell;
    })
    .join("");

  return (
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#f0f4f8;padding:36px 0;font-family:Segoe UI,Arial,sans-serif;'>" +
    "<tr><td align='center'>" +
    "<table width='600' cellpadding='0' cellspacing='0' style='background:#ffffff;border-radius:16px;overflow:hidden;'>" +

    // HEADER
    "<tr>" +
    "<td style='background:#ff8000;text-align:center;padding:36px 24px 30px;'>" +
    "<img src='" + logoUrl + "' width='160px' alt='VBT' style='display:block;margin:0 auto 18px;' />" +
    "<h1 style='margin:0;color:#ffffff;font-size:24px;font-weight:700;'>Application Received!</h1>" +
    "<p style='margin:8px 0 0;color:rgba(255,255,255,0.85);font-size:14px;'>Thank you for applying to VisionBird Technologies</p>" +
    "</td>" +
    "</tr>" +

    // GREETING
    "<tr>" +
    "<td style='padding:32px 32px 16px;'>" +
    "<p style='margin:0;font-size:16px;color:#222;line-height:1.7;'>Hi <strong style='color:#ff6600;'>" + data.name + "</strong>,</p>" +
    "<p style='margin:10px 0 0;font-size:14px;color:#555;line-height:1.8;'>" +
    "Thank you for applying for <strong style='color:#111;'>" + jobTitle + "</strong> at " +
    "<strong style='color:#ff6600;'>VisionBird Technologies</strong>. " +
    "We have successfully received your application, and our recruitment team will carefully review your profile." +
    "</p>" +
    "</td>" +
    "</tr>" +

    // WHAT HAPPENS NEXT
    "<tr>" +
    "<td style='padding:8px 32px 16px;'>" +
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#fff8f0;border:1px solid #ffd199;border-radius:12px;overflow:hidden;'>" +
    "<tr>" +
    "<td style='background:#ff8000;padding:12px 18px;'>" +
    "<p style='margin:0;color:#fff;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;'>&#128161; &nbsp;What Happens Next?</p>" +
    "</td>" +
    "</tr>" +
    "<tr>" +
    "<td style='padding:20px 20px;'>" +
    "<table width='100%' cellpadding='0' cellspacing='0'>" +
    "<tr><td style='padding:8px 0;'>" +
    "<table cellpadding='0' cellspacing='0'><tr>" +
    "<td style='width:28px;height:28px;border-radius:50%;background:#fff3e6;border:1.5px solid #ffd199;text-align:center;line-height:28px;font-size:11px;font-weight:800;color:#ff6600;'>01</td>" +
    "<td style='padding-left:12px;font-size:13px;color:#444;line-height:1.7;'>Our HR team will carefully review your application and resume to evaluate your qualifications for the position.</td>" +
    "</tr></table>" +
    "</td></tr>" +
    "<tr><td style='padding:8px 0;'>" +
    "<table cellpadding='0' cellspacing='0'><tr>" +
    "<td style='width:28px;height:28px;border-radius:50%;background:#fff3e6;border:1.5px solid #ffd199;text-align:center;line-height:28px;font-size:11px;font-weight:800;color:#ff6600;'>02</td>" +
    "<td style='padding-left:12px;font-size:13px;color:#444;line-height:1.7;'>If you are shortlisted, you will receive a separate email containing your interview date, time, and all the necessary instructions.</td>" +
    "</tr></table>" +
    "</td></tr>" +
    "<tr><td style='padding:8px 0;'>" +
    "<table cellpadding='0' cellspacing='0'><tr>" +
    "<td style='width:28px;height:28px;border-radius:50%;background:#fff3e6;border:1.5px solid #ffd199;text-align:center;line-height:28px;font-size:11px;font-weight:800;color:#ff6600;'>03</td>" +
    "<td style='padding-left:12px;font-size:13px;color:#444;line-height:1.7;'>Please keep an eye on the email address you provided during your application, as all future updates and communications from our HR team will be sent there.</td>" +
    "</tr></table>" +
    "</td></tr>" +
    "</table>" +
    "</td>" +
    "</tr>" +
    "</table>" +
    "</td>" +
    "</tr>" +

    // PROGRESS STEPS
    "<tr>" +
    "<td style='padding:8px 32px 16px;'>" +
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#fafafa;border:1px solid #eeeeee;border-radius:12px;padding:20px 16px;'>" +
    "<tr><td>" +
    "<p style='margin:0 0 18px;text-align:center;font-size:11px;font-weight:700;color:#bbb;text-transform:uppercase;letter-spacing:1.2px;'>Application Progress</p>" +
    "<table width='100%' cellpadding='0' cellspacing='0'><tr>" +
    progressCells +
    "</tr></table>" +
    "</td></tr>" +
    "</table>" +
    "</td>" +
    "</tr>" +

    // CLOSING
    "<tr>" +
    "<td style='padding:8px 32px 28px;border-top:1px solid #f5f5f5;margin-top:8px;'>" +
    "<p style='margin:0;font-size:14px;color:#555;line-height:1.8;'>We appreciate your interest in joining VisionBird Technologies and wish you the very best. &#128522;</p>" +
    "<p style='margin:14px 0 0;font-size:14px;color:#333;'>Warm Regards,<br><strong style='color:#ff6600;'>VisionBird Technologies &mdash; HR Team</strong></p>" +
    "</td>" +
    "</tr>" +

    // FOOTER
    "<tr>" +
    "<td style='background:#111111;text-align:center;padding:22px;'>" +
    "<p style='margin:0 0 4px;color:#ff8000;font-size:13px;font-weight:700;letter-spacing:0.5px;'>VISIONBIRD TECHNOLOGIES</p>" +
    "<p style='margin:0;color:#666;font-size:12px;'>&#169; " + year + " &nbsp;&#183;&nbsp; Please do not reply to this email</p>" +
    "</td>" +
    "</tr>" +

    "</table>" +
    "</td></tr>" +
    "</table>"
  );
}
// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
function ApplySection() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [genderError, setGenderError] = useState(false);
  const [emailLogo, setEmailLogo] = useState("");
  const [holidayDates, setHolidayDates] = useState([]);
  const [isCheckingSlots, setIsCheckingSlots] = useState(false);
  const [myReservation, setMyReservation] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isReserving, setIsReserving] = useState(false);
  const [slotExpiredModal, setSlotExpiredModal] = useState(false);
  const [timerHovered, setTimerHovered] = useState(false);
  const userId = useRef(getOrCreateUserId()).current;
  const tabId = useRef(getOrCreateTabId()).current;
  const countdownRef = useRef(null);
  const errorRef = useRef(null);

  useEffect(() => {
    const fetchJobData = async () => {
      try {
        const jobRef = doc(db, "jobPosts", id);
        const jobSnap = await getDoc(jobRef);
        if (jobSnap.exists()) {
          const data = jobSnap.data();
          setJob({ ...data, title: (data.title || "").replace(/&#x2F;/gi, "/") });
        } else {
          console.log("❌ Job not found!");
        }

        // Fetch email logo from Firestore
        const logosRef = doc(db, "logos", "xrSCDlcVzp10becG8bnW");
        const logosSnap = await getDoc(logosRef);
        if (logosSnap.exists()) {
          const logoData = logosSnap.data();
          setEmailLogo(logoData.emailLogo || "");
        }

      } catch (error) {
        console.error("❌ Error fetching job:", error);
        setSubmitting(false);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchJobData();
  }, [id]);

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const snapshot = await getDocs(collection(db, "slots"));
        const list = snapshot.docs.map((doc) => {
          const data = doc.data();
          return { label: data.slot, time: data.time };
        });
        setAvailableSlots(list);
      } catch (error) {
        console.error("Error fetching slots:", error);
        toast.error(" Failed to load time slots. Please try again.");
      }
    };
    fetchSlots();
  }, []);


  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
  }, []);

  // ── Load public holidays at mount (so the check is synchronous on submit) ──
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const ref = doc(db, "settings", "publicHolidays");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setHolidayDates(snap.data().dates || []);
        }
      } catch (err) {
        // Rules may block unauthenticated reads — log for visibility.
        console.warn("[PublicHolidays] Could not load holiday dates:", err.code || err.message);
      }
    };
    fetchHolidays();
  }, []);


  const [formData, setFormData] = useState({
    name: "",
    cnic: "",
    phone: "",
    email: "",
    Location: "",
    interviewDate: "",
    interviewSlot: "",
    resumeOption: "file",
    resumeFile: null,
    resumeLink: "",
    candidateGender: "",
  });

  const navigate = useNavigate();
  const [fileName, setFileName] = useState("");
  const [cnicBlockedModal, setCnicBlockedModal] = useState({ visible: false, daysLeft: 0 });

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, resumeFile: file });
    setFileName(file ? file.name : "");
  };

  const uploadResumeToCloudinary = async (file) => {
    const url = `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/auto/upload`;
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    const res = await fetch(url, { method: "POST", body: data });
    const json = await res.json();
    return json.secure_url;
  };
  const startCountdown = (expiresAt) => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    const tick = () => {
      const remaining = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        setMyReservation(null);
        setSecondsLeft(0);
        setFormData(prev => ({ ...prev, interviewSlot: "" }));
        try { localStorage.removeItem(`vbt_reservation_${id}`); } catch { }
        setSlotExpiredModal(true);
      }
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
  };

  useEffect(() => {
    let savedDocId;
    try { savedDocId = localStorage.getItem(`vbt_reservation_${id}`); } catch { }
    if (!savedDocId) return;
    restoreReservation(db, savedDocId, userId, tabId).then((data) => {
      if (data) {
        const dateValidation = validateInterviewDate(data.interviewDate, 3, holidayDates);
        if (dateValidation.valid) {
          setMyReservation({ docId: savedDocId, expiresAt: data.expiresAt, slotTime: data.slotTime });
          setFormData(prev => ({ ...prev, interviewDate: data.interviewDate, interviewSlot: data.slotTime }));
          startCountdown(data.expiresAt);
        } else {
          releaseReservation(db, savedDocId);
          try { localStorage.removeItem(`vbt_reservation_${id}`); } catch { }
        }
      } else {
        try { localStorage.removeItem(`vbt_reservation_${id}`); } catch { }
      }
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, []);

  const refreshSlots = async () => {
    if (!formData.interviewDate || isCheckingSlots || isReserving) return;
    setIsCheckingSlots(true);
    try {
      const formattedDate = formatToAppDateString(formData.interviewDate);
      const now = new Date();
      const [jobAppSnap, oppAppSnap, interviewsSnap, resSnap] = await Promise.all([
        getDocs(query(collection(db, "jobApplications"), where("interviewDate", "==", formattedDate))),
        getDocs(query(collection(db, "opportunityApplications"), where("interviewDate", "==", formattedDate))),
        getDocs(query(collection(db, "interviews"), where("date", "==", formattedDate))),
        getDocs(query(collection(db, "slotReservations"), where("interviewDate", "==", formData.interviewDate))),
      ]);
      const bookedFromJobApps = jobAppSnap.docs.map((d) => d.data().interviewSlot);
      const bookedFromOppApps = oppAppSnap.docs.map((d) => d.data().interviewSlot);
      const bookedFromInterviews = interviewsSnap.docs
        .filter((d) => d.data().status !== "rejected" && d.data().status !== "completed")
        .map((d) => d.data().time);
      const tempBooked = [];
      resSnap.docs.forEach((d) => {
        const data = d.data();
        if (data.status === "temporary") {
          if (data.expiresAt.toDate() <= now) {
            deleteDoc(doc(db, "slotReservations", d.id)).catch(() => { });
          } else if (data.tabId !== tabId) {
            tempBooked.push(data.slotTime);
          }
        }
      });
      setBookedSlots([...bookedFromJobApps, ...bookedFromOppApps, ...bookedFromInterviews, ...tempBooked]);
      toast.success("Slots refreshed.");
    } catch {
      toast.error("Could not refresh slots. Try again.");
    } finally {
      setIsCheckingSlots(false);
    }
  };

  const handleSlotChange = async (e) => {
    const selectedTime = e.target.value;
    if (!selectedTime || isReserving) return;
    if (myReservation && myReservation.slotTime === selectedTime) return;

    setIsReserving(true);

    if (myReservation) {
      releaseReservation(db, myReservation.docId);
      try { localStorage.removeItem(`vbt_reservation_${id}`); } catch { }
      setMyReservation(null);
      setSecondsLeft(0);
      if (countdownRef.current) clearInterval(countdownRef.current);
    }

    try {
      const { docId, expiresAt } = await createSlotReservation(
        db, formData.interviewDate, selectedTime, userId, tabId, holidayDates
      );
      try { localStorage.setItem(`vbt_reservation_${id}`, docId); } catch { }
      setMyReservation({ docId, expiresAt, slotTime: selectedTime });
      setFormData(prev => ({ ...prev, interviewSlot: selectedTime }));
      startCountdown(expiresAt);
      toast.success("Slot reserved! Complete your application within 2 minutes.");
    } catch (err) {
      if (err.message === "SLOT_TEMPORARILY_RESERVED") {
        toast.error("This slot is temporarily reserved by another applicant. Please try a different slot or refresh.");
      } else if (err.message === "SLOT_PERMANENTLY_TAKEN") {
        toast.error("This slot has already been booked. Please select another.");
      } else if (err.message) {
        toast.error(err.message);
      } else {
        toast.error("Could not reserve slot. Try again.");
      }
      setFormData(prev => ({ ...prev, interviewSlot: "" }));
    } finally {
      setIsReserving(false);
    }
  };

  const handleDateChange = async (e) => {
    const selectedDateInput = e.target.value;

    // Release any existing reservation when the user picks a new date
    if (myReservation) {
      releaseReservation(db, myReservation.docId);
      try { localStorage.removeItem(`vbt_reservation_${id}`); } catch { }
      setMyReservation(null);
      setSecondsLeft(0);
      if (countdownRef.current) clearInterval(countdownRef.current);
    }

    if (!selectedDateInput) {
      setFormData({ ...formData, interviewDate: "", interviewSlot: "" });
      return;
    }

    if (isPublicHoliday(selectedDateInput)) {
      showHolidayToast(selectedDateInput);
      setFormData({ ...formData, interviewDate: "", interviewSlot: "" });
      return;
    }

    const dateValidation = validateInterviewDate(selectedDateInput, 3, holidayDates);
    if (!dateValidation.valid) {
      toast.error(dateValidation.error);
      setFormData({ ...formData, interviewDate: "", interviewSlot: "" });
      return;
    }

    const formattedDate = formatToAppDateString(selectedDateInput);

    setFormData({
      ...formData,
      interviewDate: selectedDateInput,
      interviewSlot: "",
    });

    setIsCheckingSlots(true);

    try {
      const now = new Date();
      const [jobAppSnap, oppAppSnap, interviewsSnap, resSnap] = await Promise.all([
        getDocs(query(collection(db, "jobApplications"), where("interviewDate", "==", formattedDate))),
        getDocs(query(collection(db, "opportunityApplications"), where("interviewDate", "==", formattedDate))),
        getDocs(query(collection(db, "interviews"), where("date", "==", formattedDate))),
        getDocs(query(collection(db, "slotReservations"), where("interviewDate", "==", selectedDateInput))),
      ]);

      const bookedFromJobApps = jobAppSnap.docs.map((d) => d.data().interviewSlot);
      const bookedFromOppApps = oppAppSnap.docs.map((d) => d.data().interviewSlot);
      const bookedFromInterviews = interviewsSnap.docs
        .filter((d) => d.data().status !== "rejected" && d.data().status !== "completed")
        .map((d) => d.data().time);

      const tempBooked = [];
      resSnap.docs.forEach((d) => {
        const data = d.data();
        if (data.status === "temporary") {
          if (data.expiresAt.toDate() <= now) {
            deleteDoc(doc(db, "slotReservations", d.id)).catch(() => { });
          } else if (data.tabId !== tabId) {
            tempBooked.push(data.slotTime);
          }
        }
      });

      const allBooked = [...bookedFromJobApps, ...bookedFromOppApps, ...bookedFromInterviews, ...tempBooked];
      setBookedSlots(allBooked);

      if (allBooked.length >= availableSlots.length) {
        toast.error("All interview slots for this date are already booked.");
        setFormData({ ...formData, interviewDate: "", interviewSlot: "" });
      }
    } catch (error) {
      console.error("Error checking slots:", error);
      toast.error("Error checking available slots. Try again.");
    } finally {
      setIsCheckingSlots(false);
    }
  };

  const handleGenderChange = (e) => {
    const selectedGender = e.target.value;
    if (
      (job.gender === "Male" && selectedGender === "Female") ||
      (job.gender === "Female" && selectedGender === "Male")
    ) {
      setGenderError(true);
      setTimeout(() => {
        errorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
      setFormData({
        name: "",
        cnic: "",
        phone: "",
        email: "",
        Location: "",
        interviewDate: "",
        interviewSlot: "",
        resumeOption: "file",
        resumeFile: null,
        resumeLink: "",
        candidateGender: "",
      });
      setFileName("");
    } else {
      setGenderError(false);
      setFormData({ ...formData, candidateGender: selectedGender });
    }
  };

  const filteredSlots = availableSlots
    .filter((slot) => !bookedSlots.includes(slot.time))
    .sort((a, b) => {
      const numA = parseInt(a.label.replace("Slot ", ""));
      const numB = parseInt(b.label.replace("Slot ", ""));
      return numA - numB;
    });

  // ─── SEND EMAILS ON FORM SUBMIT ─────────────────────────────────────────
  // 1. Admin email  — full details for internal review.
  // 2. Candidate email — lightweight acknowledgment, no interview details.
  const sendEmailNotification = async (formData, jobTitle, resumeURL) => {
    const cleanTitle = decodeHtml(jobTitle);

    // 1. ADMIN EMAIL (Sent immediately)
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          to_email: process.env.REACT_APP_ADMIN_EMAIL,
          email: process.env.REACT_APP_ADMIN_EMAIL,
          subject: "New Application — " + cleanTitle + " (" + formData.name + ")",
          message: buildAdminEmail(formData, jobTitle, resumeURL, emailLogo),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
    } catch (error) {
      console.error("Admin email notification failed:", error);
    }

    // 2. ⏳ CRUCIAL FIX: Wait 1.5 seconds so the corporate server doesn't flag it as spam
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // 3. CANDIDATE EMAIL (Sent after the delay)
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          to_email: formData.email,
          email: formData.email,
          subject: "We've received your application for " + cleanTitle + " — VisionBird Technologies",
          message: buildApplicationAckEmail(formData, cleanTitle, emailLogo),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
    } catch (error) {
      console.error("Candidate acknowledgment email failed:", error);
    }
  };


  const checkApplicantRestrictions = async (db, cnic, phone, email) => {
  const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
  const now = Date.now();

  try {
    const normCnic = cnic.trim();
    const normPhone = phone.trim();
    const normEmail = email.trim().toLowerCase();

    const [cnicSnap, emailSnap, phoneSnap] = await Promise.all([
      getDocs(query(collection(db, "jobApplications"), where("cnic", "==", normCnic))),
      getDocs(query(collection(db, "jobApplications"), where("email", "==", normEmail))),
      getDocs(query(collection(db, "jobApplications"), where("phone", "==", normPhone))),
    ]);

    const combinedDocs = new Map();
    [...cnicSnap.docs, ...emailSnap.docs, ...phoneSnap.docs].forEach((doc) => {
      combinedDocs.set(doc.id, doc.data());
    });

    let maxDaysLeft = 0;
    let isBlocked = false;

    for (const data of combinedDocs.values()) {
      const tsField = data.statusUpdatedAt || data.createdAt;
      if (!tsField) continue;

      let ts = 0;
      if (typeof tsField.toDate === "function") {
        ts = tsField.toDate().getTime();
      } else if (tsField.seconds) {
        ts = tsField.seconds * 1000;
      } else if (typeof tsField === "number") {
        ts = tsField;
      } else {
        continue;
      }

      const elapsed = now - ts;
      if (elapsed < THIRTY_DAYS) {
        const daysLeft = Math.ceil((THIRTY_DAYS - elapsed) / (24 * 60 * 60 * 1000));
        if (daysLeft > maxDaysLeft) maxDaysLeft = daysLeft;
        isBlocked = true;
      }
    }

    if (isBlocked) {
      return { type: "HARD_BLOCK", daysLeft: maxDaysLeft };
    }
  } catch (err) {
    console.error("Restriction check failed:", err);
  }

  return { type: "NONE", daysLeft: 0 };
};

  // ── PUBLIC HOLIDAY CHECK (synchronous — state loaded at mount) ───────────
  const isPublicHoliday = (dateStr) => {
    const targetDate = dateStr || formatDateToISO(new Date()); // "YYYY-MM-DD" local
    return holidayDates.some(h => (typeof h === "object" ? h.date === targetDate : h === targetDate));
  };

  const getHolidayNote = (dateStr) => {
    const targetDate = dateStr || formatDateToISO(new Date());
    const holiday = holidayDates.find(h => (typeof h === "object" ? h.date === targetDate : h === targetDate));
    return (holiday && typeof holiday === "object") ? holiday.note : "";
  };

  // ── PROFESSIONAL HOLIDAY TOAST ────────────────────────────────────────────
  const showHolidayToast = (dateStr) => {
    toast.dismiss("holiday-toast");
    const note = getHolidayNote(dateStr);
    let dateObj;
    if (dateStr) {
      const [year, month, day] = dateStr.split("-");
      dateObj = new Date(Number(year), Number(month) - 1, Number(day));
    } else {
      dateObj = new Date();
    }
    const dateLabel = dateObj.toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    toast(
      <div style={{ display: "flex", gap: "14px", alignItems: "flex-start", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ background: "#FFEDD5", color: "#EA580C", padding: "10px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className="fas fa-calendar-times" style={{ fontSize: "20px" }}></i>
        </div>
        <div style={{ lineHeight: 1.5 }}>
          <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 15, color: "#1F2937" }}>
            Date Unavailable
          </p>
          <p style={{ margin: 0, fontSize: 13, color: "#4B5563", paddingRight: "4px" }}>
            We apologize, but our team will be away on <strong style={{ color: "#111827" }}>{dateLabel}</strong>
            {note ? ` currently because of ${note}` : ` due to a holiday`}.
            Please select another date.
          </p>
        </div>
      </div>,
      {
        toastId: "holiday-toast",
        position: "bottom-left",
        autoClose: 7000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        icon: false,
        style: {
          background: "#ffffff",
          border: "1px solid #E5E7EB",
          borderRadius: "16px",
          padding: "16px",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          width: "380px",
        },
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // ── Public holiday gate (synchronous) ────────────────────────────────
    if (formData.interviewDate && isPublicHoliday(formData.interviewDate)) {
      showHolidayToast(formData.interviewDate);
      setSubmitting(false);
      return;
    }

    // ── 3-day restriction & weekend gate ─────────────────────────────────
    const dateValidation = validateInterviewDate(formData.interviewDate, 3, holidayDates);
    if (!dateValidation.valid) {
      toast.error(dateValidation.error);
      setSubmitting(false);
      return;
    }

    const {
      name,
      cnic,
      phone,
      email,
      Location,
      interviewDate,
      interviewSlot,
      resumeOption,
      resumeFile,
      resumeLink,
    } = formData;

    if (name.trim().length < 4) {
      toast.error("Name must be at least 4 characters long.");
      setSubmitting(false);
      return;
    }
    if (!/^\d{13}$/.test(formData.cnic)) {
      toast.error("Please enter a valid 13-digit CNIC number (digits only).");
      setSubmitting(false);
      return;
    }
    if (!/^\d+$/.test(phone)) {
      toast.error("Phone number must contain only numbers.");
      setSubmitting(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      setSubmitting(false);
      return;
    }
    if (!Location || !interviewDate || !interviewSlot) {
      toast.error("Please fill in all required fields before submitting.");
      setSubmitting(false);
      return;
    }

    // ── Slot reservation validation ────────────────────────────────────────
    if (!myReservation || secondsLeft <= 0) {
      toast.error("Your slot reservation has expired. Please re-select a time slot.");
      setSubmitting(false);
      return;
    }

   // ── 30-Day Multi-Field Restriction Check ───────────────────────────────
    const restriction = await checkApplicantRestrictions(db, cnic, phone, email);
    if (restriction.type === "HARD_BLOCK") {
      setCnicBlockedModal({ visible: true, daysLeft: restriction.daysLeft });
      setSubmitting(false);
      return;
    }

    try {
      let resumeURL = "";
      if (resumeOption === "file" && resumeFile) {
        resumeURL = await uploadResumeToCloudinary(resumeFile);
      } else if (resumeOption === "link") {
        resumeURL = resumeLink;
      }

      const formattedDate = formatToAppDateString(formData.interviewDate);

      const trimmedName = name.trim();
      const trimmedCnic = cnic.trim();
      const trimmedPhone = phone.trim();
      const trimmedEmail = email.trim();
      const trimmedLocation = Location.trim();

      // Lock the slot permanently before writing the application
      confirmReservation(db, myReservation.docId);
      try { localStorage.removeItem(`vbt_reservation_${id}`); } catch { }
      if (countdownRef.current) clearInterval(countdownRef.current);

      await addDoc(collection(db, "jobApplications"), {
        name: trimmedName,
        cnic: trimmedCnic,
        phone: trimmedPhone,
        email: trimmedEmail,
        Location: trimmedLocation,
        interviewDate: formattedDate,
        interviewSlot,
        resumeURL,
        jobTitle: job?.title || "N/A",
        jobId: id,
        createdAt: serverTimestamp(),
      });

      const trimmedFormData = {
        ...formData,
        name: trimmedName,
        cnic: trimmedCnic,
        phone: trimmedPhone,
        email: trimmedEmail,
        Location: trimmedLocation
      };

      await sendEmailNotification(trimmedFormData, job?.title || "N/A", resumeURL);

      navigate("/application-success", { state: { name } });
    } catch (error) {
      console.error("Error adding document:", error);
      navigate("/application-error");
      setSubmitting(false);
    }
  };

  const minSelectableDate = getMinSelectableDate(3, holidayDates);
  const minDateStr = formatDateToISO(minSelectableDate);
  const maxDate = new Date(minSelectableDate);
  maxDate.setDate(minSelectableDate.getDate() + 30);
  const lastDay = formatDateToISO(maxDate);

  if (loading)
    return (
      <div style={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <LoadingAnimation text="Loading job details..." />
      </div>
    );
  if (!job) return <p style={{ textAlign: "center" }}>Job not found.</p>;

  return (
    <>
      <div className="apply- padding-container">
        <div className="page-wrapper">
          <div className="apply-now">
            <div className="apply-from">
              {genderError ? (
                <div
                  className="gender-error-message"
                  style={{
                    height: "400px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    padding: "30px 20px",
                    backgroundColor: "white",
                    borderRadius: "12px",
                    marginTop: "60px",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                    color: "#fff",
                    gap: "20px",
                  }}
                >
                  <img
                    src="/assets/Images/office-pana.svg"
                    alt="Office Illustration"
                    style={{
                      maxHeight: "240px",
                      width: "100%",
                      objectFit: "contain",
                    }}
                  />
                  <p
                    style={{
                      fontSize: "18px",
                      lineHeight: "1.5",
                      fontWeight: "500",
                      maxWidth: "600px",
                      color: "black",
                    }}
                  >
                    This position is currently only open for {job.gender}{" "}
                    candidates. We are working to make it available for both male
                    and female soon. Thank you for your interest!
                  </p>
                </div>
              ) : (
                <div className="form">
                  <h1>Apply Now</h1>

                  <p className="upload-resume" style={{ fontWeight: "bold" }}>
                    Upload or Link Your Resume
                  </p>

                  <div className="resume-option" style={{ marginBottom: "10px" }}>
                    <label>
                      <input
                        type="radio"
                        name="resumeOption"
                        value="file"
                        checked={formData.resumeOption === "file"}
                        onChange={handleChange}
                      />
                      Upload File
                    </label>
                    <label style={{ marginLeft: "15px" }}>
                      <input
                        type="radio"
                        name="resumeOption"
                        value="link"
                        checked={formData.resumeOption === "link"}
                        onChange={handleChange}
                      />
                      Paste Link
                    </label>
                  </div>

                  {formData.resumeOption === "file" ? (
                    <div className="resume" style={{ width: "100%" }}>
                      <label
                        htmlFor="resumeFile"
                        style={{
                          fontSize: "20px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "fit-content",
                          padding: "8px 20px",
                          borderRadius: "12px",
                          backgroundColor: "#fff",
                          border: "1px solid #ccc",
                          cursor: "pointer",
                          position: "relative",
                          transition: "border-color 0.2s",
                        }}
                      >
                        {/* Dynamic Centered Text */}
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#666",
                            fontWeight: "500",
                            pointerEvents: "none",
                          }}
                        >
                          {fileName
                            ? "Upload another resume"
                            : "Upload your resume"}
                        </div>

                        <input
                          key={fileName}
                          type="file"
                          id="resumeFile"
                          name="resumeFile"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          required
                          style={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0,
                            cursor: "pointer",
                            width: "100%",
                            height: "100%",
                          }}
                        />
                      </label>

                      {/* File Name Display */}
                      {fileName && (
                        <div
                          style={{
                            marginTop: "10px",
                            padding: "8px 12px",
                            backgroundColor: "#eef2ff",
                            color: "#374151",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: "500",
                            display: "inline-block",
                            border: "1px solid #d1d5db",
                          }}
                        >
                          📄 {fileName}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="resume-link">
                      <input
                        type="url"
                        id="resumeLink"
                        name="resumeLink"
                        placeholder="Paste your resume link here"
                        style={{
                          width: "100%",
                          padding: "12px",
                          border: "1px solid #ccc",
                          borderRadius: "8px",
                        }}
                        value={formData.resumeLink}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  )}

                  <form className="form-data" onSubmit={handleSubmit}>
                    <div className="form-name">
                      <label htmlFor="name">Name</label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-gender">
                      <label htmlFor="candidateGender">Select Gender</label>
                      <select
                        name="candidateGender"
                        id="candidateGender"
                        value={formData.candidateGender}
                        onChange={handleGenderChange}
                        required
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>

                    <div className="form-phone">
                      <label htmlFor="interviewDate">Select Interview Date</label>
                      <input
                        type="date"
                        name="interviewDate"
                        id="interviewDate"
                        value={formData.interviewDate}
                        onChange={handleDateChange}
                        min={minDateStr}
                        max={lastDay}
                        required
                      />
                    </div>

                    {formData.interviewDate && (
                      <div className="form-phone">
                        <label htmlFor="interviewSlot" style={{ userSelect: "none", cursor: "default" }}>Select Interview Slot</label>

                        {/* Timer + refresh — vertically centered in the 90px form-phone div */}
                        <div style={{
                          position: "absolute", top: "50%", right: "0",
                          transform: "translateY(-50%)",
                          display: "flex", alignItems: "center", gap: "8px",
                        }}>
                          {/* Circular countdown ring */}
                          {myReservation && secondsLeft > 0 && (() => {
                            const total = Math.round(RESERVATION_TTL_MS / 1000);
                            const r = 8;
                            const circ = 2 * Math.PI * r;
                            const dashoffset = circ * (1 - secondsLeft / total);
                            const urgent = secondsLeft <= 15;
                            return (
                              <div
                                style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", cursor: "default" }}
                                onMouseEnter={() => setTimerHovered(true)}
                                onMouseLeave={() => setTimerHovered(false)}
                              >
                                <svg width="22" height="22" viewBox="0 0 20 20"
                                  style={{ display: "block", transform: "rotate(-90deg)", flexShrink: 0 }}>
                                  <circle cx="10" cy="10" r={r} fill="none"
                                    stroke="rgba(255,140,0,0.2)" strokeWidth="2.5" />
                                  <circle cx="10" cy="10" r={r} fill="none"
                                    stroke={urgent ? "#e53e3e" : "#ff8c00"} strokeWidth="2.5"
                                    strokeDasharray={circ}
                                    strokeDashoffset={dashoffset}
                                    strokeLinecap="round"
                                    style={{ transition: "stroke-dashoffset 1s linear, stroke 0.4s ease" }}
                                  />
                                </svg>
                                {timerHovered && (
                                  <div style={{
                                    position: "absolute", bottom: "calc(100% + 8px)", right: 0,
                                    background: "#0f172a", color: "#f8fafc",
                                    padding: "6px 11px", borderRadius: "8px",
                                    fontSize: "12px", fontWeight: "500", whiteSpace: "nowrap",
                                    boxShadow: "0 6px 16px rgba(0,0,0,0.22)",
                                    zIndex: 100, pointerEvents: "none",
                                  }}>
                                    Slot reserved — {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, "0")} remaining
                                    <div style={{
                                      position: "absolute", top: "100%", right: "8px",
                                      width: 0, height: 0,
                                      borderLeft: "5px solid transparent",
                                      borderRight: "5px solid transparent",
                                      borderTop: "5px solid #0f172a",
                                    }} />
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Refresh pill-button */}
                          <button
                            type="button"
                            onClick={refreshSlots}
                            disabled={isCheckingSlots || isReserving}
                            style={{
                              display: "flex", alignItems: "center", gap: "4px",
                              height: "24px", padding: "0 10px", borderRadius: "999px",
                              margin: 0, marginRight: "6px",
                              background: "none", border: "1.5px solid #e2e8f0",
                              cursor: isCheckingSlots || isReserving ? "not-allowed" : "pointer",
                              color: "#64748b", fontSize: "12px", fontWeight: "500",
                              opacity: isCheckingSlots || isReserving ? 0.5 : 1,
                              transition: "border-color 0.2s, color 0.2s, background 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              if (!isCheckingSlots && !isReserving) {
                                e.currentTarget.style.borderColor = "#94a3b8";
                                e.currentTarget.style.background = "#f8fafc";
                                e.currentTarget.style.color = "#334155";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = "#e2e8f0";
                              e.currentTarget.style.background = "none";
                              e.currentTarget.style.color = "#64748b";
                            }}
                          >
                            <i className={`fas fa-sync-alt${isCheckingSlots ? " fa-spin" : ""}`} style={{ fontSize: "11px" }}></i>
                            Refresh
                          </button>
                        </div>

                        <select
                          name="interviewSlot"
                          id="interviewSlot"
                          value={formData.interviewSlot}
                          onChange={handleSlotChange}
                          disabled={isCheckingSlots || isReserving}
                          required
                        >
                          <option value="">
                            {isCheckingSlots
                              ? "-- Checking available slots --"
                              : isReserving
                                ? "-- Reserving your slot... --"
                                : filteredSlots.length === 0
                                  ? "-- No slots available — try refreshing --"
                                  : "-- Select Time Slot --"}
                          </option>
                          {!isCheckingSlots && filteredSlots.map((slot, i) => (
                            <option key={i} value={slot.time}>{slot.label}: {slot.time}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="form-cnic">
                      <label htmlFor="cnic">CNIC Number</label>
                      <input
                        type="text"
                        name="cnic"
                        id="cnic"
                        placeholder="Enter your correct 13 digits CNIC number"
                        value={formData.cnic}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 13)
                            setFormData({ ...formData, cnic: value });
                        }}
                        maxLength="13"
                        required
                      />
                      {formData.cnic &&
                        formData.cnic.length > 0 &&
                        formData.cnic.length < 13 && (
                          <p
                            style={{
                              display: "none",
                              color: "red",
                              fontSize: "13px",
                              marginTop: "4px",
                            }}
                          >
                            CNIC must be 13 digits.
                          </p>
                        )}
                    </div>

                    <div className="form-phone">
                      <label htmlFor="phone">Phone</label>
                      <input
                        type="text"
                        name="phone"
                        id="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-email">
                      <label htmlFor="email">Email</label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-email">
                      <label htmlFor="Location">Location</label>
                      <input
                        type="text"
                        name="Location"
                        id="Location"
                        value={formData.Location}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <button type="submit" disabled={submitting || genderError}>
                      {submitting ? "Submitting..." : "Apply"}
                    </button>
                  </form>
                </div>
              )}
            </div>

            <div className="job-detail">
              <div className="job-top">
                <div className="top-detail">
                  <div className="job-date">
                    <p>{job.date}</p>
                  </div>
                  <div className="job-icon">
                    <img
                      src={
                        job.jobIcon ||
                        "/assets/Images/desktop_duotone_fill (1).png"
                      }
                      alt="job icon"
                      width="24px"
                      height="24px"
                    />
                  </div>
                </div>
                <div className="job-tital">
                  <p>{job.category}</p>
                  <p className="tital">{job.title}</p>
                </div>
                <div className="job-Req">
                  {job.requirements?.map((req, index) => (
                    <div className="req" key={index}>
                      <p>{req}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="job-buttom">
                <div className="buttom-down">
                  <h1>Salary</h1>
                  <p>{job.salary}</p>
                  <h1>Job Description</h1>
                  <p style={{ fontFamily: "'poppins-regular', sans-serif" }} dangerouslySetInnerHTML={{ __html: job.description }} />  {/* Fixed */}
                  <h1>Gender Validity</h1>
                  <p>{job.gender}</p>
                  <h1>Location</h1>
                  <p className="office-location">{job.location}</p>
                  {job.jobTypes?.map((type, index) => (
                    <div className="time" key={index}>
                      <p>{type}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Slot Reservation Expired Modal ───────────────────────────────── */}
      {slotExpiredModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "20px" }}>
          <div style={{ background: "#ffffff", borderRadius: "20px", padding: "44px 36px", maxWidth: "440px", width: "100%", textAlign: "center", boxShadow: "0 32px 64px rgba(0,0,0,0.2)" }}>
            <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "linear-gradient(135deg, #fff5f5, #fed7d7)", border: "2px solid #fc8181", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: "30px" }}>
              ⏰
            </div>
            <h2 style={{ margin: "0 0 12px", color: "#1a202c", fontSize: "22px", fontWeight: "700" }}>Reservation Expired</h2>
            <p style={{ margin: "0 0 28px", color: "#4a5568", fontSize: "15px", lineHeight: "1.7" }}>
              Your 2-minute slot reservation has expired. Please select a new time slot to continue your application.
            </p>
            <button
              onClick={() => setSlotExpiredModal(false)}
              style={{ background: "linear-gradient(135deg, #FF8C00, #E67E00)", color: "white", border: "none", padding: "14px 40px", borderRadius: "50px", fontSize: "15px", fontWeight: "700", cursor: "pointer", boxShadow: "0 4px 16px rgba(255,140,0,0.35)" }}
            >
              Select New Slot
            </button>
          </div>
        </div>
      )}

      {/* ── CNIC 30-Day Restriction Modal (mirrors ApplyReservation) ── */}
      {cnicBlockedModal.visible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              padding: "44px 36px",
              maxWidth: "480px",
              width: "100%",
              textAlign: "center",
              boxShadow: "0 32px 64px rgba(0,0,0,0.2)",
            }}
          >
            {/* Icon */}
            <div
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #fff8f0 0%, #ffecd2 100%)",
                border: "2px solid #ffd199",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
                fontSize: "30px",
              }}
            >
              🕐
            </div>

            <h2
              style={{
                margin: "0 0 12px",
                color: "#1a202c",
                fontSize: "22px",
                fontWeight: "700",
                lineHeight: "1.3",
              }}
            >
              Application Under Review Period
            </h2>

            <p
              style={{
                margin: "0 0 8px",
                color: "#4a5568",
                fontSize: "15px",
                lineHeight: "1.7",
              }}
            >
              You have recently submitted an application to{" "}
              <strong style={{ color: "#ff8000" }}>VisionBird Technologies</strong>.
              We kindly ask you to wait at least{" "}
              <strong>30 days</strong> before submitting a new application.
            </p>

            <p
              style={{
                margin: "0 0 28px",
                color: "#718096",
                fontSize: "14px",
                lineHeight: "1.6",
              }}
            >
              We truly appreciate your continued interest and encourage you to keep
              developing your skills in the meantime.
            </p>

            <button
              onClick={() => setCnicBlockedModal({ visible: false, daysLeft: 0 })}
              style={{
                background: "linear-gradient(135deg, #FF8C00 0%, #E67E00 100%)",
                color: "white",
                border: "none",
                padding: "14px 40px",
                borderRadius: "50px",
                fontSize: "15px",
                fontWeight: "700",
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(255,140,0,0.35)",
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,140,0,0.45)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 16px rgba(255,140,0,0.35)";
              }}
            >
              I Understand
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ApplySection;